import { isProduction } from './environment-helper';

// Structured Data helper functions - only work in production
class StructuredDataHelper {
  static baseUrl = 'https://www.coachingsearchengine.com';

  // Organization schema for the website
  static generateOrganizationSchema(lang = 'tr') {
    if (!isProduction()) return null;

    const name = 'Coaching Search Engine';
    const description =
      lang === 'tr'
        ? 'AI destekli platformumuzla size en uygun profesyonel koçları keşfedin.'
        : 'Discover the most suitable professional coaches with our AI-powered platform.';

    return {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: name,
      description: description,
      url: this.baseUrl,
      logo: `${this.baseUrl}/assets/logo.png`,
      foundingDate: '2024',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'info@coachingsearchengine.com',
        availableLanguage: ['Turkish', 'English'],
      },
      sameAs: [
        'https://www.linkedin.com/company/coaching-search-engine',
        'https://twitter.com/CoachingSearchEng',
        'https://www.facebook.com/CoachingSearchEngine',
      ],
      potentialAction: {
        '@type': 'SearchAction',
        target: `${this.baseUrl}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    };
  }

  // WebSite schema for homepage
  static generateWebSiteSchema(lang = 'tr') {
    if (!isProduction()) return null;

    const name = 'Coaching Search Engine';
    const description =
      lang === 'tr'
        ? 'AI destekli platformumuzla size en uygun profesyonel koçları keşfedin.'
        : 'Discover the most suitable professional coaches with our AI-powered platform.';

    return {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: name,
      description: description,
      url: this.baseUrl,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${this.baseUrl}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
      publisher: {
        '@type': 'Organization',
        name: name,
        logo: `${this.baseUrl}/assets/logo.png`,
      },
    };
  }

  // Person schema for coach profiles
  static generateCoachPersonSchema(coach, lang = 'tr') {
    if (!isProduction() || !coach) return null;

    const coachName = `${coach.firstName} ${coach.lastName}`;
    const coachUrl = `${this.baseUrl}/${lang}/coach/${coach.slug}`;

    const description =
      coach.bio ||
      (lang === 'tr'
        ? `${coachName} ile profesyonel koçluk hizmeti alın.`
        : `Get professional coaching services with ${coachName}.`);

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: coachName,
      givenName: coach.firstName,
      familyName: coach.lastName,
      description: description,
      url: coachUrl,
      image: coach.profileImage || coach.imageUrl,
      jobTitle: coach.headline || (lang === 'tr' ? 'Profesyonel Koç' : 'Professional Coach'),
      worksFor: {
        '@type': 'Organization',
        name: 'Coaching Search Engine',
      },
    };

    // Add address if available
    if (coach.city || coach.country) {
      schema.address = {
        '@type': 'PostalAddress',
        addressLocality: coach.city,
        addressCountry: coach.country,
      };
    }

    // Add social media profiles
    const socialProfiles = [coach.linkedin, coach.facebook, coach.instagram, coach.website].filter(Boolean);

    if (socialProfiles.length > 0) {
      schema.sameAs = socialProfiles;
    }

    // Add credentials/certificates
    if (coach.certificates && coach.certificates.length > 0) {
      schema.hasCredential = coach.certificates.map((cert) => ({
        '@type': 'EducationalOccupationalCredential',
        name: cert.name,
        description: cert.description,
        credentialCategory: 'certificate',
        recognizedBy: {
          '@type': 'Organization',
          name: cert.issuer,
        },
      }));
    }

    // Add specializations
    if (coach.categories && coach.categories.length > 0) {
      schema.knowsAbout = coach.categories.map((cat) => cat.name || cat);
    }

    return schema;
  }

  // Service schema for coaching services
  static generateCoachingServiceSchema(coach, lang = 'tr') {
    if (!isProduction() || !coach) return null;

    const coachName = `${coach.firstName} ${coach.lastName}`;
    const serviceName =
      lang === 'tr' ? `${coachName} - Profesyonel Koçluk Hizmeti` : `${coachName} - Professional Coaching Service`;

    const description =
      lang === 'tr'
        ? `${coachName} ile kişisel ve profesyonel gelişim koçluğu hizmeti alın.`
        : `Get personal and professional development coaching services with ${coachName}.`;

    return {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: serviceName,
      description: description,
      provider: {
        '@type': 'Person',
        name: coachName,
        image: coach.profileImage || coach.imageUrl,
      },
      serviceType: 'Coaching',
      category: coach.categories ? coach.categories.map((cat) => cat.name || cat) : [],
      areaServed: coach.country || 'Turkey',
      availableChannel: {
        '@type': 'ServiceChannel',
        serviceUrl: `${this.baseUrl}/${lang}/coach/${coach.slug}`,
        servicePhone: coach.phone,
        serviceSmsNumber: coach.phone,
      },
    };
  }

  // CollectionPage schema for category pages
  static generateCategoryCollectionSchema(category, lang = 'tr') {
    if (!isProduction() || !category) return null;

    const categoryName = category.getName(lang);
    const categoryDescription = category.getDescription(lang);
    const categoryUrl = `${this.baseUrl}${category.getUrl(lang)}`;

    return {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: categoryName,
      description: categoryDescription,
      url: categoryUrl,
      mainEntity: {
        '@type': 'ItemList',
        name: `${categoryName} ${lang === 'tr' ? 'Koçları' : 'Coaches'}`,
        description: categoryDescription,
        numberOfItems: category.coachCount || 0,
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: lang === 'tr' ? 'Ana Sayfa' : 'Home',
            item: `${this.baseUrl}/${lang}`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: lang === 'tr' ? 'Kategoriler' : 'Categories',
            item: `${this.baseUrl}/${lang}/${lang === 'tr' ? 'kategoriler' : 'categories'}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: categoryName,
            item: categoryUrl,
          },
        ],
      },
    };
  }

  // ItemList schema for category listings
  static generateCategoryListSchema(categories, lang = 'tr') {
    if (!isProduction() || !categories) return null;

    const listName = lang === 'tr' ? 'Koçluk Kategorileri' : 'Coaching Categories';

    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: listName,
      description:
        lang === 'tr'
          ? 'Profesyonel koçluk hizmetleri için kategori listesi'
          : 'Category list for professional coaching services',
      numberOfItems: categories.length,
      itemListElement: categories.map((category, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: category.getName(lang),
        description: category.getDescription(lang),
        url: `${this.baseUrl}${category.getUrl(lang)}`,
      })),
    };
  }

  // FAQ schema for category pages
  static generateCategoryFAQSchema(category, lang = 'tr') {
    if (!isProduction() || !category) return null;

    const categoryName = category.getName(lang);

    const faqs =
      lang === 'tr'
        ? [
            {
              question: `${categoryName} koçu nasıl bulabilirim?`,
              answer: `${categoryName} alanında uzman koçları platformumuzda bulabilirsiniz. Arama yaparak size en uygun koçu seçebilirsiniz.`,
            },
            {
              question: `${categoryName} koçluğu nasıl çalışır?`,
              answer: `${categoryName} koçluğu, belirlediğiniz hedeflere ulaşmanız için kişiselleştirilmiş rehberlik ve destek sağlar.`,
            },
            {
              question: `Koçluk seansları nasıl yapılır?`,
              answer:
                'Koçluk seansları online veya yüz yüze gerçekleştirilebilir. Koçunuzla iletişime geçerek detayları konuşabilirsiniz.',
            },
          ]
        : [
            {
              question: `How can I find a ${categoryName} coach?`,
              answer: `You can find expert coaches in ${categoryName} on our platform. Search and select the most suitable coach for you.`,
            },
            {
              question: `How does ${categoryName} coaching work?`,
              answer: `${categoryName} coaching provides personalized guidance and support to help you achieve your defined goals.`,
            },
            {
              question: 'How are coaching sessions conducted?',
              answer:
                'Coaching sessions can be conducted online or face-to-face. Contact your coach to discuss the details.',
            },
          ];

    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    };
  }

  // Review schema for coaches (when reviews are available)
  static generateCoachReviewSchema(coach, reviews = []) {
    if (!isProduction() || !coach || !reviews.length) return null;

    const avgRating = reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length;

    return {
      '@context': 'https://schema.org',
      '@type': 'AggregateRating',
      itemReviewed: {
        '@type': 'Person',
        name: `${coach.firstName} ${coach.lastName}`,
      },
      ratingValue: avgRating.toFixed(1),
      reviewCount: reviews.length,
      bestRating: 5,
      worstRating: 1,
    };
  }

  // Complete structured data for homepage
  static generateHomepageStructuredData(lang = 'tr') {
    if (!isProduction()) return null;

    return [this.generateOrganizationSchema(lang), this.generateWebSiteSchema(lang)].filter(Boolean);
  }

  // Complete structured data for coach page
  static generateCoachPageStructuredData(coach, lang = 'tr', reviews = []) {
    if (!isProduction()) return null;

    const schemas = [this.generateCoachPersonSchema(coach, lang), this.generateCoachingServiceSchema(coach, lang)];

    if (reviews && reviews.length > 0) {
      schemas.push(this.generateCoachReviewSchema(coach, reviews));
    }

    return schemas.filter(Boolean);
  }

  // Complete structured data for category page
  static generateCategoryPageStructuredData(category, lang = 'tr') {
    if (!isProduction()) return null;

    return [
      this.generateCategoryCollectionSchema(category, lang),
      this.generateCategoryFAQSchema(category, lang),
    ].filter(Boolean);
  }

  // Complete structured data for category list page
  static generateCategoryListPageStructuredData(categories, lang = 'tr') {
    if (!isProduction()) return null;

    return [this.generateCategoryListSchema(categories, lang)].filter(Boolean);
  }
}

export default StructuredDataHelper;
